import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { evidenceFilesApi, verificationRunsApi } from '../../utils/api';
import { fmtDate, fmtDateTime } from './dates';
import UploadReceipt, { receiptFor } from './UploadReceipt';

// PRD-030 WU-10 — the Evidence surface, built to the ratified mockup
// (rev 2): evidence grouped under the Outcome it supports, a drop zone
// per Outcome, every file stamped with who added it and when, and the
// visible run-coalescing countdown with Start now. Both roles can add
// evidence (dev plan WU-10; the file chip names whose side added it).
// Plain-English copy only — no internal names on screen.

const FILE_TYPE_LABELS = {
  'text/csv': 'CSV',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'application/json': 'JSON',
  'text/plain': 'TXT',
};
export const ACCEPT = Object.keys(FILE_TYPE_LABELS).join(',');

// One plain-words message for every upload failure, on every surface. The
// route's own 415 text lists raw MIME types ("application/vnd.openxml…") —
// customer copy never carries that (CLAUDE.md customer vocabulary; WU-C QA
// round 2, F4). Both upload paths call this, so they cannot drift apart.
export function uploadErrorMessage(filename, err) {
  const code = err?.response?.data?.error_code;
  if (code === 'unsupported_media_type' || code === 'mime_mismatch') {
    return `${filename}: Verdict reads CSV, Excel, image, JSON, and text files. This one is a different type.`;
  }
  if (code === 'file_too_large') {
    return `${filename}: larger than the 10 MB limit. Split the export or add a smaller date range.`;
  }
  if (code === 'agreement_quota_exceeded') {
    return `${filename}: this Agreement's evidence storage is full. Delete files you no longer need, then add it again.`;
  }
  if (code === 'file_required') {
    return `${filename}: the file came through empty. Export it again and add it.`;
  }
  // "Try again" is useless advice for a permission or identity problem, so
  // these say what would actually move the person forward (WU-C QA M3).
  if (code === 'not_found' || code === 'no_member_identity') {
    return `${filename}: this workspace can't add evidence to this Agreement. Ask an admin to check your access.`;
  }
  if (code === 'internal_error' || code === 'upload_failed') {
    return `${filename}: it didn't go through on our side. Try again; if it repeats, the file may need re-exporting.`;
  }
  return `${filename}: it didn't go through. Try again.`;
}

function typeLabel(mime) {
  return FILE_TYPE_LABELS[mime] || 'FILE';
}

function SideChip({ side, name }) {
  // Agency/Client — never seller/buyer on screen.
  const isAgency = side === 'seller';
  return (
    <span
      className={`text-[10.5px] px-2 py-0.5 rounded border ${
        isAgency ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-amber-800 bg-amber-50 border-amber-200'
      }`}
      title={isAgency ? 'Added by the agency' : 'Added by the client'}
    >
      {name}
    </span>
  );
}

function DropZone({ onFiles, disabled }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`border-2 border-dashed rounded-xl px-5 py-5 text-center cursor-pointer mb-2 transition-colors ${
        over ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-gray-50 hover:border-primary-300'
      } ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
      onClick={() => inputRef.current && inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length) onFiles([...e.dataTransfer.files]);
      }}
      data-testid="evidence-drop"
    >
      <div className="text-sm font-semibold text-gray-900">Drop a file here, or click to add</div>
      <div className="text-[11.5px] text-gray-600 mt-0.5">CSV, Excel, images, JSON · up to 10 MB</div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length) onFiles([...e.target.files]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// Live countdown to a queued check's start (F6: the wait is visible, with
// an escape hatch — never a silent multi-minute pause after an upload).
// When the window elapses, onElapsed re-polls until the run actually
// leaves `queued` — the banner resolves itself, never sticking on
// "starting…" (WU-10 QA M1).
function RunCountdown({ run, isAgency, onStartNow, onElapsed }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = run && run.status === 'queued' && run.queued_reason !== 'daily_cap'
    && (!run.not_before || new Date(run.not_before).getTime() <= now);
  useEffect(() => {
    if (!elapsed) return undefined;
    const t = setInterval(() => onElapsed(), 3000);
    return () => clearInterval(t);
  }, [elapsed, onElapsed]);
  if (!run || run.status !== 'queued') return null;

  if (run.queued_reason === 'daily_cap') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-[13px] px-4 py-2.5 mb-4">
        High demand today — the delivery check is queued and starts automatically. We'll email you when it begins.
      </div>
    );
  }

  const secs = run.not_before ? Math.max(0, Math.ceil((new Date(run.not_before).getTime() - now) / 1000)) : 0;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-[13px] px-4 py-2.5 mb-4"
      data-testid="run-countdown"
    >
      <span className="flex-1">
        {secs > 0
          ? <>Delivery check starts in <b className="tabular-nums">{secs}s</b> — waiting briefly in case more files are coming.</>
          : 'Delivery check is starting…'}
      </span>
      {isAgency && secs > 0 && (
        <button type="button" className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3 py-1.5" onClick={onStartNow}>
          Start now
        </button>
      )}
    </div>
  );
}

// WU-A′ §4 — the filename-mismatch nudge. Each outcome's plan names its
// evidence sources ("Where it came from" carries filenames like
// crm-lead-export.csv). Pull those names out so an upload landing under
// the wrong outcome gets a soft, non-blocking notice — process is
// plumbing: the upload always proceeds.
const SOURCE_FILENAME_RE = /\b[\w][\w.-]*\.(?:csv|xlsx|png|jpe?g|json|txt)\b/gi;

export function namedSourcesFor(outcome) {
  // String() not a truthiness guard: a non-string here would throw inside
  // .match and blank the whole Delivery tab (WU-C QA M1).
  const text = String(outcome?.delivery?.evidence_source || '');
  // Names come back AS THE SPEC WROTE THEM — the CTA renders these, and a
  // lower-cased "crm-export.csv" is not the string the user sees in Finder
  // (WU-C QA N2). Dedup is case-insensitive; comparison lower-cases at the
  // call site.
  const seen = new Set();
  const names = [];
  for (const n of text.match(SOURCE_FILENAME_RE) || []) {
    const key = n.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(n);
  }
  return names;
}

// → null when the name fits (or the spec names nothing), else the notice
// copy. Exported shape kept simple for the copy-lint test.
export function filenameMismatchNotice(fileName, outcome, allOutcomes) {
  const expected = namedSourcesFor(outcome);
  if (expected.length === 0) return null;
  const lower = String(fileName || '').toLowerCase();
  const lc = (list) => list.map((n) => n.toLowerCase());
  if (lc(expected).includes(lower)) return null;
  const other = (allOutcomes || []).find(
    (o) => o !== outcome && lc(namedSourcesFor(o)).includes(lower)
  );
  if (other) {
    return `${fileName} is named as evidence for "${other.name}", and this outcome's plan names ${expected.join(', ')}. The file was added here — move it if it belongs with the other outcome.`;
  }
  return `This outcome's plan names ${expected.join(', ')} — ${fileName} looks like a different export. The file was added; double-check it is the right one.`;
}

export default function EvidenceSurface({ agreementId, outcomes, isAgency }) {
  const [files, setFiles] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadNotices, setUploadNotices] = useState([]);
  // WU-F: the product says it got the file. Until now an upload produced a
  // row in a list and nothing else — no receipt, no sign a check was
  // coming. An open loop at the one moment the user has just done work.
  const [receipt, setReceipt] = useState(null);
  const [busyOutcome, setBusyOutcome] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [fRes, rRes] = await Promise.all([
        evidenceFilesApi.list(agreementId),
        verificationRunsApi.list(agreementId),
      ]);
      setFiles(fRes.data.evidence_files || []);
      setRuns(rRes.data.runs || []);
      setError(null);
    } catch (err) {
      setError("Couldn't load evidence.");
    } finally {
      setLoaded(true);
    }
  }, [agreementId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Queued run (the countdown target), if any.
  const queuedRun = useMemo(() => runs.find((r) => r.status === 'queued') || null, [runs]);

  // Group files under the Outcome whose deliverables they support.
  const { byOutcome, unmapped } = useMemo(() => {
    const deliverableToOutcome = new Map();
    for (const o of outcomes) {
      for (const d of o.deliverables || []) deliverableToOutcome.set(d.id, o.outcomeId);
    }
    const grouped = new Map(outcomes.map((o) => [o.outcomeId, []]));
    const rest = [];
    for (const f of files) {
      const oid = f.deliverable_id ? deliverableToOutcome.get(f.deliverable_id) : null;
      if (oid && grouped.has(oid)) grouped.get(oid).push(f);
      else rest.push(f);
    }
    return { byOutcome: grouped, unmapped: rest };
  }, [files, outcomes]);

  const upload = async (outcome, list) => {
    const deliverableId = (outcome.deliverables || [])[0]?.id || null;
    setBusyOutcome(outcome.outcomeId);
    const errors = [];
    const notices = [];
    const added = [];
    for (const file of list) {
      try {
        await evidenceFilesApi.upload(agreementId, file, deliverableId);
        added.push(file.name);
        // Soft nudge AFTER the upload lands — never a gate (WU-A′ §4).
        const notice = filenameMismatchNotice(file.name, outcome, outcomes);
        if (notice) notices.push(notice);
      } catch (err) {
        errors.push(uploadErrorMessage(file.name, err));
      }
    }
    setUploadErrors(errors);
    setUploadNotices(notices);
    // Names the files and the outcome they joined. A count alone would hide
    // a failure inside a success, so the count is stated only when some of
    // the batch failed.
    const next = receiptFor(added, list.length, outcome.name);
    setReceipt(next ? { ...next, outcomeId: outcome.outcomeId } : null);
    setBusyOutcome(null);
    await refresh();
  };

  const startNow = async () => {
    if (!queuedRun) return;
    try {
      await verificationRunsApi.startNow(agreementId, queuedRun.id);
      await refresh();
    } catch (err) {
      setError("Couldn't start the check — try again.");
    }
  };

  // Never flash "No evidence yet" while the first fetch is in flight —
  // an empty state must mean empty, not still-loading (WU-14 U3).
  if (!loaded) {
    return <div className="card p-6 text-center text-gray-600 text-sm" data-testid="evidence-loading">Loading evidence…</div>;
  }
  if (error) {
    return (
      <div className="card p-6 text-center text-sm" data-testid="evidence-error">
        <div className="text-gray-600 mb-3">{error}</div>
        <button type="button" className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3.5 py-2" onClick={refresh}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <RunCountdown run={queuedRun} isAgency={isAgency} onStartNow={startNow} onElapsed={refresh} />

      {uploadErrors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-[12.5px] px-4 py-2.5 mb-4" data-testid="upload-errors">
          {uploadErrors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {uploadNotices.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-[12.5px] px-4 py-2.5 mb-4" data-testid="filename-mismatch-notice">
          {uploadNotices.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      )}

      {outcomes.map((o, idx) => {
        const group = byOutcome.get(o.outcomeId) || [];
        return (
          <div key={o.outcomeId} className="mb-5" data-testid={`evidence-group-${idx}`}>
            <div className="flex items-baseline gap-2.5 mb-2 px-0.5">
              <span className="text-[13px] font-semibold text-gray-900">Outcome {idx + 1} · {o.name}</span>
              {o.activeTo && (
                <span className="text-[11px] text-gray-600 tabular-nums ml-auto">due {fmtDate(o.activeTo, { year: false })}</span>
              )}
            </div>
            <UploadReceipt
              receipt={receipt && receipt.outcomeId === o.outcomeId ? receipt : null}
              onDismiss={() => setReceipt(null)}
              compact
            />
            <DropZone onFiles={(list) => upload(o, list)} disabled={busyOutcome === o.outcomeId} />
            {group.length === 0 ? (
              <div className="text-[12px] text-gray-600 px-3.5 py-2.5 border border-dashed border-gray-200 rounded-lg">
                No evidence yet for this outcome.
              </div>
            ) : (
              group.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg mb-1.5 text-[13px]">
                  <span className="text-[9.5px] font-extrabold tracking-wide text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                    {typeLabel(f.mime_type)}
                  </span>
                  <span className="font-semibold text-gray-900">{f.filename}</span>
                  <span className="flex-1" />
                  <span className="text-[11px] text-gray-600 tabular-nums">uploaded {fmtDateTime(f.created_at)}</span>
                  <SideChip side={f.uploaded_by_side} name={f.uploaded_by_name} />
                  {f.deliverable_id && (
                    <Link to={`/agreements/${agreementId}/deliverables/${f.deliverable_id}/evidence`}
                      className="text-[12px] font-semibold text-primary-600 hover:text-primary-700">Open</Link>
                  )}
                </div>
              ))
            )}
          </div>
        );
      })}

      {unmapped.length > 0 && (
        <div className="mb-5">
          <div className="text-[13px] font-semibold text-gray-900 mb-2 px-0.5">Whole-agreement evidence</div>
          {unmapped.map((f) => (
            <div key={f.id} className="flex items-center gap-3 px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg mb-1.5 text-[13px]">
              <span className="text-[9.5px] font-extrabold tracking-wide text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                {typeLabel(f.mime_type)}
              </span>
              <span className="font-semibold text-gray-900">{f.filename}</span>
              <span className="flex-1" />
              <span className="text-[11px] text-gray-600 tabular-nums">uploaded {fmtDateTime(f.created_at)}</span>
              <SideChip side={f.uploaded_by_side} name={f.uploaded_by_name} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
